// Card de Credenciais Zenvia da tela de Provedores — RHF + Zod:
// tenantId, clientId (copiável) e clientSecret WRITE-ONLY (o valor
// atual NUNCA é lido/exibido; quando clientSecretSet, o placeholder
// indica "definido" e salvar exige digitar um valor NOVO). Salvar
// abre AlertDialog confirmando a troca do secret; "Testar conexão"
// dispara o POST de teste e mostra a latência no toast.
import { zodResolver } from "@hookform/resolvers/zod"
import { KeyRoundIcon, PlugZapIcon } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { SectionCard } from "@/components/shared/detail-components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "@/components/ui/password-input"
import type { NotificationZenviaConfigType } from "@/types/notification-center"

import {
  useTestZenviaConnection,
  useUpdateZenviaCredentials,
} from "../provider-mutations"

const zenviaPrefix =
  "pages.notificationCenter.providersPage.zenvia"

// Schema do form — clientSecret é OBRIGATÓRIO: salvar sempre exige
// digitar um secret novo (write-only, nunca ecoamos o atual).
const buildCredentialsSchema = (requiredMessage: string) =>
  z.object({
    tenantId: z.string().min(1, requiredMessage),
    clientId: z.string().min(1, requiredMessage),
    clientSecret: z.string().min(1, requiredMessage),
  })
type CredentialsFormValues = z.infer<
  ReturnType<typeof buildCredentialsSchema>
>

type ZenviaCredentialsCardProps = {
  whiteLabelId: string
  zenvia: NotificationZenviaConfigType
}

export const ZenviaCredentialsCard = ({
  whiteLabelId,
  zenvia,
}: ZenviaCredentialsCardProps) => {
  const { t } = useTranslation()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [pendingValues, setPendingValues] =
    useState<CredentialsFormValues | null>(null)
  const updateCredentials =
    useUpdateZenviaCredentials(whiteLabelId)
  const testConnection =
    useTestZenviaConnection(whiteLabelId)

  const form = useForm<CredentialsFormValues>({
    resolver: zodResolver(
      buildCredentialsSchema(t(`${zenviaPrefix}.required`))
    ),
    defaultValues: {
      tenantId: zenvia.tenantId,
      clientId: zenvia.clientId,
      // Write-only: o campo SEMPRE nasce vazio, mesmo com secret
      // definido — o valor atual nunca chega ao front.
      clientSecret: "",
    },
  })

  // Submit válido → guarda os valores e confirma a troca do secret.
  const handleSubmit = form.handleSubmit((values) => {
    setPendingValues(values)
    setIsConfirmOpen(true)
  })

  const handleConfirm = () => {
    if (!pendingValues) return
    updateCredentials.mutate(pendingValues)
    // Write otimista: limpa o secret digitado após enviar.
    form.resetField("clientSecret")
    setPendingValues(null)
  }

  return (
    <SectionCard
      title={t(`${zenviaPrefix}.title`)}
      icon={
        <KeyRoundIcon
          className="size-4"
          aria-hidden="true"
        />
      }
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={testConnection.isPending}
          onClick={() => testConnection.mutate()}
        >
          <PlugZapIcon
            className="mr-1.5 size-4"
            aria-hidden="true"
          />
          {t(`${zenviaPrefix}.testConnection`)}
        </Button>
      }
    >
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="space-y-4 p-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="tenantId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${zenviaPrefix}.tenantId`)}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${zenviaPrefix}.clientId`)}
                  </FormLabel>
                  <div className="flex items-center gap-1">
                    <FormControl>
                      <Input
                        className="font-mono"
                        {...field}
                      />
                    </FormControl>
                    <ClipboardButton text={field.value} />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="clientSecret"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t(`${zenviaPrefix}.clientSecret`)}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder={
                      zenvia.clientSecretSet
                        ? t(
                            `${zenviaPrefix}.secretSetPlaceholder`
                          )
                        : t(
                            `${zenviaPrefix}.secretUnsetPlaceholder`
                          )
                    }
                    {...field}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t(`${zenviaPrefix}.secretWriteOnlyHint`)}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end">
            <Button type="submit">
              {t(`${zenviaPrefix}.save`)}
            </Button>
          </div>
        </form>
      </Form>

      {/* Confirmação da troca do secret (write destrutivo). */}
      <AlertDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${zenviaPrefix}.confirmTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(`${zenviaPrefix}.confirmDescription`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${zenviaPrefix}.confirmCancel`)}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              {t(`${zenviaPrefix}.confirmAction`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  )
}
